import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormElement, Field, Form, FormHandle } from '@progress/kendo-react-form'
import { MultiFactorVerifiedDto, UserMultiFactorSettingsJoinedDto, MultiFactorTypeEnum, VerifyGoogleAuthenticatorCodeDto } from '@v1UsersApi'
import MessageAlert from 'components/Alerts/MessageAlert/MessageAlert'
import FormInput from 'components/Form/FormInput'
import FormSkeleton from 'components/Form/FormSkeleton'
import { getErrors, focusFirstError } from 'core/helpers'
import { V1ApiUsersClient } from 'core/http-common'
import { useState, useRef, useEffect } from 'react'
import { Row, Col, Button, Badge } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { setMultFactorVerified } from 'state/actions/userActions'
import { AppDispatch } from 'state/store'
import { IRootState, AlertMessage } from 'types'

interface IFormState {
    code: string
}

interface IProps {
    onVerified?: (dto: MultiFactorVerifiedDto) => void
}

const GoogleAuthenticatorVerifyForm = ({ onVerified }: IProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const loading = useSelector((state: IRootState) => state.app.loading)
    const formRef = useRef<FormHandle>(null)
    const [alert, setAlert] = useState<AlertMessage>()
    const [userGoogleSettings, setUserGoogleSettings] = useState<UserMultiFactorSettingsJoinedDto>()

    const fetch = async () => {
        try {
            await dispatch(startLoadingAction())
            const response = await V1ApiUsersClient.getUserMultiFactorSettings()
            const userGoogleSettings = response.data.find(f => f.multiFactorTypeId === MultiFactorTypeEnum.Google)
            setUserGoogleSettings(userGoogleSettings)
        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    useEffect(() => {
        fetch()
    }, [])

    const verifyCode = async (form: IFormState) => {
        try {
            dispatch(startLoadingAction())
            setAlert({ variant: 'info', messages: [] })
            let response = await V1ApiUsersClient.verifyGoogleAuthenticatorCode(new VerifyGoogleAuthenticatorCodeDto({ code: form.code }))
            dispatch(setMultFactorVerified(response))
            if (onVerified)
                onVerified(response)

        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    return (
        <div>
            <h5 className='text-center'>
                {!userGoogleSettings
                    ? <FormSkeleton style={{ height: '100px' }} />
                    : <span>
                        <img src={userGoogleSettings?.logoUrl} style={{ height: '50px' }} />
                        Google Authenticator {userGoogleSettings?.recommended && <Badge bg={'success'}>Recommended</Badge>}
                    </span>
                }
            </h5>

            {!userGoogleSettings
                ? <FormSkeleton style={{ height: '50px' }} />
                : <div>
                    <Form
                        ref={formRef}
                        onSubmit={(e) => verifyCode(e as IFormState)}
                        render={(formRenderProps) => (
                            <FormElement className='text-center'>
                                {!userGoogleSettings
                                    ? <FormSkeleton style={{ height: '50px' }} />
                                    : <p>Open the Google Authenticator app on your mobile device and find the 6 digit code for 'System Admin' and enter it below.</p>
                                }

                                <Row className='mb-2'>
                                    <Col xs={12}>
                                        {!userGoogleSettings
                                            ? <FormSkeleton />
                                            : <Field
                                                name='code'
                                                id='code'
                                                component={FormInput}
                                                label='Code:'
                                                autoComplete='false'
                                                autoFocus
                                            />
                                        }
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={12}>
                                        {!userGoogleSettings
                                            ? <FormSkeleton style={{ height: '50px' }} />
                                            : <Button
                                                className='w-100'
                                                variant='primary'
                                                type='submit'
                                                disabled={!formRenderProps.allowSubmit || loading}
                                                onClick={e => focusFirstError(formRenderProps)}>
                                                <FontAwesomeIcon icon={['fas', 'arrow-circle-right']} /> Verify
                                            </Button>
                                        }
                                    </Col>
                                </Row>
                            </FormElement>
                        )}
                    />
                    <MessageAlert alert={alert} />
                </div>
            }
        </div>
    )
}
export default GoogleAuthenticatorVerifyForm